import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { isMobile } from 'react-device-detect';

import { useTranslation, Trans } from 'react-i18next';

import { useTheme, IconButton, Menu, MenuItem, Box, Drawer, Typography } from '@mui/material';
import { Article, MoreVert } from '@mui/icons-material';

import { Canvas, useThree } from '@react-three/fiber';
import { Stats } from '@react-three/drei';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import * as THREE from 'three';

import Background from '../../../partials/project/view/Background';
import Lights from '../partials/Lights';
import Cameras from '../../../partials/project/view/Cameras';
import Controls from '../../../partials/project/view/Controls';
import Floor from '../../../partials/project/view/Floor';
import { LoadingScreen } from '../../../partials/LoadingScreen';
import StyledIconButton from '../../../partials/easyFloor/StyledIconButton';
import CubeIcon from '../../../icons/CubeIcon';
import CubesIcon from '../../../icons/CubesIcon';
import DrawerHeader from '../../../styled/DrawerHeader';
import BackIcon from '../../../icons/BackIcon';
import Sidebar from './Sidebar';
import Main from '../../../styled/Main';

import { calcCameraDistance } from '../../../../utils/Utils';
import apiClient from '../../../../services/api';
import store from '../../../../store';
import { setElements, setMeshId, setProjectElementId, setMesh } from '../../../../store/shortcuts/Project';
import { toggleMessageOpen } from '../../../../store/actions/Message';
import { setSelectedMeshId } from '../../../../store/actions/Project';

const SHADOWS = false;
const CAMERA_DISTANCE = 2500;

const Model = React.memo((props) => {
    const { scene, groupProject, setGroupProject } = props;
    const copiedScene = React.useMemo(() => scene.clone(), [scene]);
    const group = useRef();
    const gl = useThree();

    const selectedMeshID = useSelector((state) => state.project.selectedMeshId);

    const [originalColors, setOriginalColors] = useState([]);

    useEffect(() => {
        if (!scene) {
            return;
        }

        let colors = [];

        scene.children.forEach((children) => {
            children.traverse((object) => {
                if (object.isMesh) {
                    const color = new THREE.Color(
                        object.material.color.r,
                        object.material.color.g,
                        object.material.color.b,
                    );

                    colors[object.uuid] = color;
                }
            });
        });

        setOriginalColors(colors);

        scene.traverse((object) => {
            if (object.isMesh === true && object.material.map !== null) {
                object.material.map.anisotropy = gl?.gl.capabilities.getMaxAnisotropy();
            }
        });

        groupProject.add(scene);

        setGroupProject(groupProject);
    }, [scene]);

    useEffect(() => {
        scene.children.forEach((children) => {
            children.traverse((object) => {
                if (object.isMesh === true) {

                    object.material.color.set(originalColors[object.uuid]);

                    const { userData } = object;

                    if (userData?.EBMeshId == selectedMeshID) {
                        object.material.color.set('#00FF00');
                    }
                }
            });
        })
    }, [selectedMeshID])

    return (
        <group ref={group} dispose={null}>
            <primitive
                object={copiedScene}
                onClick={(event) => {
                    event.stopPropagation();

                    const { userData, name } = event.intersections[0].object;
                    const { EBMeshId, EBProjectElementId } = userData;
                    let parentEBProjectElementId = null;

                    if (!EBProjectElementId) {
                        const parentUserData = event.intersections[0].object?.parent?.userData;

                        parentEBProjectElementId = parentUserData?.EBProjectElementId;
                    }

                    if (selectedMeshID && selectedMeshID == EBMeshId) {
                        setSelectedMeshId(null);

                        setMeshId(null);
                        setProjectElementId(null);
                    } else {
                        setSelectedMeshId(EBMeshId);

                        setMeshId(EBMeshId);
                        setProjectElementId(EBProjectElementId ? EBProjectElementId : parentEBProjectElementId);
                    }

                    setMesh(event.intersections[0].object);
                }}
            />;
        </group>
    )
});

const ThemedBackground = () => {
    const theme = useTheme();
    return <Background color={theme.palette.background.default} />;
};

const ThemedFloor = () => {
    const theme = useTheme();
    return <Floor color={theme.palette.border.grid} />;
};

function getWindowDimensions() {
    const { innerWidth: width, innerHeight: height } = window;

    return { width, height };
}

const FloatingButtons = (props) => {
    const {
        viewType,
        setViewType,
        id,
        open,
        setOpen
    } = props;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

    useEffect(() => {
        function handleResize() {
            setWindowDimensions(getWindowDimensions());
        }

        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const style = { position: 'absolute', top: '50%', right: '20px', zIndex: 10, transform: 'translateY(-50%)' }

    return (
        <div style={style}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <StyledIconButton
                    color="primary"
                    title={t('Change View')}
                    isActive={false}
                    onClick={() => {
                        if (viewType == '3D') {
                            setViewType('2D');
                        } else {
                            setViewType('3D');
                        }
                    }}
                >
                    {viewType != '3D' ? <CubeIcon /> : <CubesIcon />}
                </StyledIconButton>

                <StyledIconButton
                    color="primary"
                    title={t('Show report')}
                    isActive={open}
                    onClick={() => { setOpen(!open); }}
                >
                    <Article />
                </StyledIconButton>
            </div>
        </div>
    );
};

function ProjectPublicPreviewV2({ data, projectElements, abort }) {
    const { t } = useTranslation();
    const theme = useTheme();
    const ref = useRef();
    const componentMounted = useRef(true);

    const projectIsLoading = useSelector((state) => state.project.projectIsLoading);
 const selectedElement = useSelector((state) => state.project.selectedMeshId);

    const [projectData, setProjectData] = useState(null);
    const [viewType, setViewType] = useState('3D');
    const [center, setCenter] = useState({ x: 1000, y: 5, z: 0 });
    const [distance, setDistance] = useState(CAMERA_DISTANCE);
    const [panZoomEnabled, setPanZoomEnabled] = useState(true);
    const [stats, setStats] = useState(0);
    const [loadedModels, setLoadedModels] = useState([]);
    const [drawerWidth, setDrawerWidth] = useState(500);
    const [open, setOpen] = useState(false);
    const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());
    const [modelsLoaded, setModelsLoaded] = useState(false);
    const [scene, setScene] = useState(null);
    const [groupProject, setGroupProject] = useState(new THREE.Group);
    const [selectedMeshID, setSelectedMeshID] = useState(null);
    const [loadingProgress, setLoadingProgress] = useState(null);
 const [isBackgroundsModuleActive, setIsBackgroundModulesActive] = useState(false);

    const is3D = viewType === '3D';
    const id = projectData?.project?.id;
    const projectIsLoaded = !!projectData;

useEffect(() => {
        if (selectedElement && isBackgroundsModuleActive) {
            setOpen(true);
        }
    }, [selectedElement]);

    useEffect(() => {
        if (windowDimensions) {
            setOpen(windowDimensions.width > 1200 ? true : false);
        }

apiClient.post('/api/modules/check', { name: 'backgrounds' }).then(response => {
            setIsBackgroundModulesActive(response?.data?.success);
        });
    }, []);

    useEffect(() => {
        if (windowDimensions.width < 650) {
            setDrawerWidth(windowDimensions.width);
        }
    }, [windowDimensions]);

    useEffect(() => {
        if (data) {
            setProjectData(data);
        }
    }, [data]);// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (projectElements && projectElements.length != 0) {
            setElements(projectElements);
        }
    }, [projectElements]);// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!projectData) {
            return;
        }

        loadModelsChunked(projectData);

    }, [projectData]);

    useEffect(() => {
        componentMounted.current = true;
        updateAsync();
        return () => { componentMounted.current = false };
    }, [projectData]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDrawerClose = () => {
        setOpen(false)
    };

    const updateAsync = async () => {
        if (componentMounted.current) {
            if (projectData !== null) {
                loadAndSetProjectElements();

                const center = calcProjectElementsCenter(projectData);
                setCenter(center);

                const distance = calcCameraDistance(center, true, 25);
                setDistance(distance);
            } else {
                setCenter(calcProjectCenter({}));
                setDistance(CAMERA_DISTANCE);
            }
        }
    };

    const calcProjectCenter = ({ w = 100, h = 100, d = 100 }) => {
        const x = w / 2;
        const y = h / 2;
        const z = d / 2;

        return { x, y, z };
    };

    const calcProjectElementsCenter = (data, model) => {
        return {
            x: Number(data.project.width) / 2,
            y: Number(data.project.height) / 2,
            z: Number(data.project.depth) / 2,
            w: Number(data.project.width),
            h: Number(data.project.height),
            d: Number(data.project.depth),
        };
    };

    const loadAndSetProjectElements = () => {
        apiClient.post('/api/projects/load-project-elements', { id: Number(id) }).then((response) => {
            if (!response.data.success) return;
            setElements(response.data.project_elements);
        }).catch(error => {
            store.dispatch(toggleMessageOpen(t('An error occurred while loading project elements'), 'error'));
        });
    };

    async function loadModelsChunked(projectData) {
        let counter = 0;

        while (counter <= projectData?.models?.length) {
            const result = await loadChunk(counter, counter + 50);
            counter += 50;

            setLoadingProgress({ loaded: counter, total: projectData?.models?.length });
        }

        setModelsLoaded(true);
    }

    function loadChunk(start, end) {
        const loader = new GLTFLoader();

        return new Promise((resolve, reject) => {
            projectData?.models.slice(start, end).forEach((url, index) => {
                loader.load(
                    url,
                    (gltf) => {
                        setLoadedModels((prevModels) => [
                            ...prevModels,
                            { url, scene: gltf.scene },
                        ]);

                        resolve(gltf);
                    },
                    undefined,
                    (error) => {
                        console.error(`Error loading model at ${url}:`, error);
                    }
                );
            });
        });
    }

    const Helper = ({ setScene, groupProject }) => {
        useEffect(() => {
            if (modelsLoaded) {
                setScene(groupProject);
            }
        }, [groupProject]);
    }

    const style = {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        flex: 1
    };

    return (
        <Box sx={{ display: 'flex', height: '100%' }}>
            <Drawer
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                        padding: '20px'
                    },
                    padding: '0px',
                    borderLeft: '1px solid ' + theme.palette.storeDrawer.borderLeft.color
                }}
                variant="persistent"
                anchor="left"
                open={open}
                PaperProps={{
                    sx: {
                        padding: '0px',
                        position: "relative",
                        backgroundColor: theme.palette.storeDrawer.background
                    }
                }}
            >
                <DrawerHeader>
                    <Typography align="center">
                        {projectData && projectData.project.name}
                    </Typography>
                    <button
                        type="button"
                        onClick={handleDrawerClose}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            background: 'none',
                            padding: '0px',
                            border: 'none',
                            cursor: 'pointer'
                        }}
                    >
                        <BackIcon />
                    </button>
                </DrawerHeader>
                <Sidebar />
            </Drawer>
            <Main open={open} drawerWidth={drawerWidth}>
                <div style={style}>
                    <Canvas
                        shadows={SHADOWS}
                        gl={{ preserveDrawingBuffer: true, precision: 'lowp' }}
                        style={{ cursor: 'pointer' }}
                        ref={ref}
                    >
                        <ThemedBackground />
                        <Lights shadows={SHADOWS} />
                        <Cameras perspective={is3D} position={{ ...center, z: distance }} />
                        <Controls rotate={is3D} target={{ ...center, z: 0 }} enabled={panZoomEnabled} />
                        {is3D && <ThemedFloor />}
                        {!abort && modelsLoaded && loadedModels.map((model, index) => (
                            <Model
                                key={index}
                                scene={model.scene}
                                selectedMeshID={selectedMeshID}
                                setSelectedMeshID={setSelectedMeshID}
                                groupProject={groupProject}
                                setGroupProject={setGroupProject}
                            />
                        ))}
                        {/*<Stats className="three-stats" showPanel={stats} onClick={() => setStats(v => v < 2 ? v + 1 : 0)} />*/}
                    </Canvas>
                    {modelsLoaded && <Helper setScene={setScene} groupProject={groupProject} />}
                    {/*projectIsLoading && <LoadingScreen text="Downloading Project" />*/}
                    {projectIsLoaded &&
                        <FloatingButtons
                            viewType={viewType}
                            setViewType={setViewType}
                            open={open}
                            setOpen={setOpen}
                            id={id}
                        />
                    }
                    {!modelsLoaded && (
                        <LoadingScreen
                            text={`Downloading Project (${loadingProgress?.loaded}/${loadingProgress?.total})`}
                        />
                    )}
                </div>
            </Main>
        </Box>
    )
}

export default ProjectPublicPreviewV2;
